import moment from 'moment';
import {addWorkingDays} from '@ey/utils';
import Utility from '../../../util/utilityfunctions';

// Constants
export const STORAGE_KEY_BASE = 'fourFortyRAS_reminder_lastShown';
export const HOURS_24_IN_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Generates a unique storage key for each engagement
 * @param {Object} engagement - The engagement object
 * @returns {string} - A unique storage key including the engagement ID
 */
export function getEngagementStorageKey(engagement) {
	return engagement?.engagementId ? `${STORAGE_KEY_BASE}_${engagement.engagementId}` : STORAGE_KEY_BASE;
}

/**
 * Checks if the current date is within 5 working days of the report release date
 * @param {Object} engagement - The engagement object containing expectedReportReleaseDate
 * @returns {boolean} - Whether the current date is within 5 working days of the report release date
 */
export function isWithinFiveWorkingDaysOfRRD(engagement) {
	if (!engagement?.expectedReportReleaseDate) {
		return false;
	}

	const currentDate = moment().startOf('day');
	const reportReleaseDate = moment(engagement.expectedReportReleaseDate).endOf('day');
	const fiveWorkingDaysBeforeRRD = moment(addWorkingDays(reportReleaseDate.valueOf(), -5)).startOf('day');

	return currentDate >= fiveWorkingDaysBeforeRRD && currentDate <= reportReleaseDate;
}

/**
 * Determines if the reminder should be shown based on when it was last displayed
 * @param {Object} engagement - The engagement object
 * @returns {boolean} - Whether the reminder should be shown
 */
export function shouldShowReminder(engagement) {
	// If no engagement ID, never show the reminder
	if (!engagement?.engagementId) {
		return false;
	}

	const storageKey = getEngagementStorageKey(engagement);
	const reminderData = Utility.loadStorage(storageKey);
	const lastShownTimestamp = reminderData.timestamp || 0;
	const currentTimestamp = Date.now();

	// Only show the reminder if 24 hours have passed since it was last shown
	return currentTimestamp - lastShownTimestamp > HOURS_24_IN_MS;
}

/**
 * Records that the reminder was shown or dismissed
 * @param {Object} engagement - The engagement object
 */
export function recordReminderShown(engagement) {
	// If no engagement ID, don't record anything
	if (!engagement?.engagementId) {
		return;
	}

	// Use engagement-specific storage key
	const storageKey = getEngagementStorageKey(engagement);

	Utility.saveStorage(storageKey, {
		timestamp: Date.now()
	});
}

/**
 * Formats the reminder message based on the current date and report release date
 * @param {Object} engagement - The engagement object
 * @param {Object} labels - The labels object containing text templates
 * @returns {string} - The formatted message template
 */
export function formatReminderMessage(engagement, labels) {
	const currentDate = moment().startOf('day');
	const reportReleaseDate = engagement?.expectedReportReleaseDate
		? moment(engagement.expectedReportReleaseDate).endOf('day')
		: moment().endOf('day');

	return labels.rrdReminderMessage
		.replace('{rrdDescription}', currentDate.isSame(reportReleaseDate, 'day') ? '{todayLowercase}' : '{approaching}')
		.replace(
			'{rrdPendingDays}',
			currentDate.isSame(reportReleaseDate, 'day')
				? '{todayLowercase}'
				: labels.rrdReminderPendingDays.replace('{0}', reportReleaseDate.diff(currentDate, 'days'))
		);
}
